// ─────────────────────────────────────────────────────────────
// useStrategyPreferences
//
// Reads/writes strategy_preferences jsonb on the user_preferences
// table. One row per user, RLS-protected.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

const DEFAULT_PREFS = { builtInEnabled: {} };

export function useStrategyPreferences(_accountId, _supabase, userId) {
  const [strategyPreferences, setLocal] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  const userIdRef = useRef(userId);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // ── Load ────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      setLocal(DEFAULT_PREFS);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await authFetch(
          `${SUPABASE_URL}/rest/v1/user_preferences` +
          `?user_id=eq.${userId}` +
          `&select=strategy_preferences` +
          `&limit=1`
        );
        const rows = await res.json();
        if (cancelled) return;
        const prefs = rows?.[0]?.strategy_preferences;
        setLocal(prefs && typeof prefs === "object" ? prefs : DEFAULT_PREFS);
      } catch (e) {
        if (!cancelled) {
          console.warn("[useStrategyPreferences] load error:", e.message);
          setLocal(DEFAULT_PREFS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  // ── Save ────────────────────────────────────────────────────
  const setStrategyPreferences = useCallback(async (newPrefs) => {
    setLocal(newPrefs);

    const currentUserId = userIdRef.current;
    if (!currentUserId) return;

    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/user_preferences?on_conflict=user_id`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Prefer": "resolution=merge-duplicates,return=minimal",
          },
          body: JSON.stringify({
            user_id: currentUserId,
            strategy_preferences: newPrefs,
          }),
        }
      );
      if (!res.ok) {
        const text = await res.text();
        console.error("[useStrategyPreferences] save error:", text);
      }
    } catch (e) {
      console.error("[useStrategyPreferences] save error:", e.message);
    }
  }, []);

  return { strategyPreferences, setStrategyPreferences, loading };
}
